import {ApiClient} from '@hconnect/apiclient'
import {useQuery, type UseQueryOptions, type UseQueryResult} from '@tanstack/react-query'
import type {AxiosError} from 'axios'
import {stringify} from 'query-string'

import {
  DEFAULT_CUSTOMIZATIONS_PAGE,
  DEFAULT_CUSTOMIZATIONS_PAGE_LIMIT,
  REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY
} from '../consts'
import type {RefractoryPlacingMethod} from '../enums'
import type {
  RefractoryChemicalDataFilter,
  RefractoryDataRange,
  RefractoryPaginated,
  RefractoryPhysicalDataFilter
} from '../types'

interface UseRefractoryCustomizationsQueryParams {
  plantUpmId: string
  page?: number
  limit?: number
  ids?: number[]
  name?: string
  manufacturerIds?: number[]
  classificationIds?: number[]
  classificationGroupIds?: number[]
  bondingIds?: number[]
  placingMethods?: RefractoryPlacingMethod[]
  chemicalData?: RefractoryChemicalDataFilter[]
  csRatio?: RefractoryDataRange
  physicalData?: RefractoryPhysicalDataFilter
  activeStatus?: string
  countryCode?: string[]
  manufacturerDateFrom?: string
  manufacturerDateTo?: string
  enabled?: boolean
}

export const useRefractoryCustomizationsQuery = (
  {
    plantUpmId,
    page,
    limit,
    ids,
    name,
    manufacturerIds,
    classificationIds,
    classificationGroupIds,
    bondingIds,
    placingMethods,
    chemicalData,
    csRatio,
    physicalData,
    activeStatus,
    countryCode,
    manufacturerDateFrom,
    manufacturerDateTo,
    enabled = true
  }: UseRefractoryCustomizationsQueryParams,
  options?: Omit<
    UseQueryOptions<RefractoryPaginated, AxiosError, RefractoryPaginated>,
    'queryKey' | 'queryFn' | 'enabled'
  >
): UseQueryResult<RefractoryPaginated, AxiosError> => {
  const resolvedPage = page ?? DEFAULT_CUSTOMIZATIONS_PAGE
  const resolvedLimit = limit ?? DEFAULT_CUSTOMIZATIONS_PAGE_LIMIT

  return useQuery<RefractoryPaginated, AxiosError, RefractoryPaginated>({
    queryKey: [
      REFRACTORIES_WITH_CUSTOMIZATION_QUERY_KEY,
      plantUpmId,
      resolvedPage,
      resolvedLimit,
      ids,
      name,
      manufacturerIds,
      classificationIds,
      classificationGroupIds,
      bondingIds,
      placingMethods,
      activeStatus,
      countryCode,
      manufacturerDateFrom,
      manufacturerDateTo,
      chemicalData,
      csRatio,
      physicalData
    ],
    queryFn: async ({signal}) => {
      const advancedParams: Record<string, string | number> = {}
      chemicalData?.forEach(({compound, min, max}, index) => {
        advancedParams[`chemicalData[${index}].compound`] = compound
        if (min !== undefined) advancedParams[`chemicalData[${index}].min`] = min
        if (max !== undefined) advancedParams[`chemicalData[${index}].max`] = max
      })
      if (csRatio?.min !== undefined) advancedParams['csRatio.min'] = csRatio.min
      if (csRatio?.max !== undefined) advancedParams['csRatio.max'] = csRatio.max
      if (physicalData) {
        const {measurementSystem, ...ranges} = physicalData
        advancedParams['physicalData.measurementSystem'] = measurementSystem
        Object.entries(ranges).forEach(([key, range]) => {
          if (range?.min !== undefined) advancedParams[`physicalData.${key}.min`] = range.min
          if (range?.max !== undefined) advancedParams[`physicalData.${key}.max`] = range.max
        })
      }

      const response = await ApiClient.axiosInstance.get<RefractoryPaginated>(
        `/plants/${plantUpmId}/refractory-product-data-catalog/refractories`,
        {
          params: {
            page: resolvedPage,
            limit: resolvedLimit,
            ids,
            name,
            manufacturerIds,
            classificationIds,
            classificationGroupIds,
            bondingIds,
            placingMethods,
            activeStatus,
            countryCode,
            manufacturerDateFrom,
            manufacturerDateTo,
            ...advancedParams
          },
          paramsSerializer: (params: Record<string, unknown>) =>
            stringify(params, {arrayFormat: 'none'}),
          signal
        }
      )
      return response.data
    },
    enabled: enabled && Boolean(plantUpmId),
    ...options
  })
}
